import { IContentModel, safeStringify } from '@ninjacart/nc-nk-core';
import { INinjaEvent, addEventParam, useQueuedEventStore } from '@ninjacart/nc-events-sdk-v2';
import {
  KissanEventType,
  UserProps,
  getSeoUrl,
  handleMarketingEvents,
  onVideoLike,
  useGlobalLoginContext,
  useProfileStore,
} from '@ninjacart/nc-nk-store';
import {
  LikeRegular,
  LikeSolidRegular,
  Paragraph,
  ShareRegular,
  XStack,
} from '@ninjacart/nc-nk-ui';
import { Share } from '@ninjacart/nc-nk-web-deps';
import { MessageCircle } from '@tamagui/lucide-icons';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, Pressable, View } from 'react-native';

export const ListFooter = ({
  itemType,
  content,
  uniqueDeviceId,
  onCommentBottomSheetOpen,
  index,
}: {
  itemType?: string;
  content: IContentModel;
  uniqueDeviceId?: string;
  onCommentBottomSheetOpen: Function;
  index: number;
}) => {
  const { t } = useTranslation();
  const [isLiked, setIsLiked] = useState<boolean>(content.liked);
  const [favCount, setFavcount] = useState<number>(content.likes_count);
  // const setOnLoginRedirect = useAppPersistenceStore((state) => state.setOnLoginRedirect);
  const isRendered = useRef<boolean>(false);
  const refItem = useRef(content?.id);
  const hasUserLoggedIn = useProfileStore((state) => state.hasUserLoggedIn);
  const enqueue = useRef(useQueuedEventStore((state) => state.enqueue)).current;
  const fields = useGlobalLoginContext(['openLoginSheet','previousPage', 'navigationParams']);
  const setNavigationParams = fields.navigationParams.set;
  const userProfile = useProfileStore((state) => state.userProfile);

  const handleLike = async () => {
    handleLikeEvent(content, isLiked ? KissanEventType.ITEM_DISLIKED : KissanEventType.ITEM_LIKED);
    if (!hasUserLoggedIn()) {
      fields['openLoginSheet'].set(true);
      fields['previousPage'].set('FEED PAGE');
      setNavigationParams({
        name: '',
        routeName: {
          name: `longVideo/${Platform.OS == 'web' ? content?.id : 'index'}`,
          params: { id: String(content?.id) },
        },
        description: `${t('page.likeOnHomePage')}`,
        screenName: 'FEED PAGE',
      });
      return;
    }

    setIsLiked(!isLiked);
    setFavcount(Math.max(0, !isLiked ? favCount + 1 : favCount - 1));
    mutateLikeAction(!isLiked);
  };

  useEffect(() => {
    setFavcount(content.likes_count);
    setIsLiked(content.liked);
  }, [content.id]);

  const handleLikeEvent = (item, eventName) => {
    const description = `${item?.description?.replace(/(<a.*?href="([^"]+)".*?<\/a>)|<[^>]*>/g, '')}`;
    const truncatedDescription =
      description && description?.trim().length > 100
        ? `${description?.trim().substring(0, 100)}...`
        : description;

    const additionalDetails = [
      addEventParam('itemId', item?.id),
      addEventParam('postOwnerId', item?.postOwnerId),
      addEventParam('actionBy', userProfile?.user?.name ?? uniqueDeviceId),
      addEventParam('postTitle', truncatedDescription),
      addEventParam('videoId', item?.id),
      addEventParam('recommendationId', item?.recommendationId),
      addEventParam('itemIndex', item?.itemIndex),
    ];

    if (item?.actionData?.content?.rankingId) {
      additionalDetails.push(
        addEventParam('referenceKey4', 'rankingId'),
        addEventParam('referenceValue4', item.actionData.content.rankingId)
      );
    }
    const likeEvent: INinjaEvent = {
      name: eventName,
      screenDetails: { screen: 'feed page' },
      eventParams: additionalDetails,
    };
    enqueue(likeEvent);
    handleMarketingEvents(KissanEventType.ITEM_LIKED, { screen: 'feed page' }, additionalDetails);
  };

  const mutateLikeAction = async (isLiked) => {
    console.log('Mutuate like action!!!');
    try {
      if (isLiked === null) {
        console.log('Undo like action');
        return;
      }

      if (isLiked) {
        // if (itemType == 'STREAM') {
        //   await likeVideo(content?.id);
        // } else {
        //   await useLikePost(content?.id);
        // }
        await onVideoLike(content?.id, 'like');
        content.liked = true;
        content.likes_count = content.likes_count + 1;
        // handleLikeEvent(content, KissanEventType.ITEM_LIKED);
      } else {
        // if (itemType == 'STREAM') {
        //   await disLikeVideo(content?.id); //Dislike
        // } else {
        //   await dislikePost(content?.id); //Dislike
        // }
        await onVideoLike(content?.id, 'none'); //Dislike
        content.liked = false;
        content.likes_count = content.likes_count - 1;
        // handleLikeEvent(content, KissanEventType.ITEM_DISLIKED);
      }
    } catch (error) {
      // rsetIsLiked(null);
      const additionalDetails = [
        addEventParam('errorMessage', error.toString()),
        addEventParam('apiError', safeStringify(error)),
      ];
      const disLikeAPIFailedEvent: INinjaEvent = {
        name: KissanEventType.DISLIKE_API_FAILED,
        screenDetails: { screen: 'feed page' },
        eventParams: additionalDetails,
      };
      enqueue(disLikeAPIFailedEvent);
      throw new Error('Error dislike data:', error);
    }
  };

  const handleShare = async (content) => {
    const additionalDetails = [
      addEventParam('itemId', content?.id),
      addEventParam('postOwnerId', content?.postOwnerId),
      addEventParam('videoId', content?.id),
      addEventParam('recommendationId', content?.recommendationId),
      addEventParam('itemUrl', content?.url),
      addEventParam('itemIndex', content?.itemIndex),
      addEventParam('referenceKey4', 'rankingId'),
      addEventParam('referenceValue4', content?.rankingId),
    ];
    const shareEvent: INinjaEvent = {
      name: KissanEventType.ITEM_SHARED,
      screenDetails: { screen: 'feed page' },
      eventParams: additionalDetails,
    };
    enqueue(shareEvent);

    handleMarketingEvents(KissanEventType.ITEM_SHARED, { screen: 'feed page' }, additionalDetails);

    try {
      const response = await getSeoUrl({
        id: content.seoUrl ? content.seoUrl : content?.id,
        type: 'Stream',
      });

      let shareUrl = response?.url;
      if (userProfile?.user?.referralCode) {
        shareUrl += `?refCode=${userProfile?.user?.referralCode}`;
      }

      // Web-style message: Title + SEO URL
      const titleText = content?.title || content?.heading || 'Check out this video';
      const message = `${titleText} \n${shareUrl}`;

      if (Platform.OS === 'web') {
        if (navigator.share) {
          await navigator.share({
            title: t('page.checkOutThisVideo'),
            text: titleText,
            url: shareUrl,
          });
        }
      } else {
        try {
          await Share.open({message});
          const shareSuccessEvent: INinjaEvent = {
            name: KissanEventType.ITEM_SHARED_SUCCESSFULLY,
            screenDetails: { screen: 'feed page' },
            eventParams: additionalDetails,
          };
          enqueue(shareSuccessEvent);
        } catch (err: any) {
          const shareFailedEvent: INinjaEvent = {
            name: KissanEventType.ITEM_SHARED_FAILED,
            screenDetails: { screen: 'feed page' },
            eventParams: [addEventParam('errorMessage', err.toString())],
          };
          enqueue(shareFailedEvent);
        }
      }
    } catch (error) {
      console.log('error', error);
    }
  };
  

  return (
    <View
      style={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 14,
        paddingVertical: 16,
        backgroundColor: 'transparent', // Make sure the background is transparent
      }}
    >
      <Pressable testID="kisan_tId_HandleLike" style={{ flex: 1, justifyContent: 'flex-start' }} onPress={handleLike}>
        <XStack justifyContent="flex-start">
          {!isLiked ? (
            <LikeRegular color={'$commentSectionTextColor'} size={22} />
          ) : (
            <LikeSolidRegular color={'$primary'} size={22} />
          )}
          <Paragraph
            color={'$commentSectionTextColor'}
            fontSize={16}
            fontWeight={'500'}
            marginLeft={5}
          >
            {favCount ?? 0}
          </Paragraph>
        </XStack>
      </Pressable>

      <Pressable testID="kisan_tId_CommentBottomSheetOpen" style={{ flex: 1 }} onPress={() => onCommentBottomSheetOpen(content, index)}>
        <XStack justifyContent="center">
          <MessageCircle color={'$commentSectionTextColor'} size={22} />
          <Paragraph
            fontSize={16}
            fontWeight={'500'}
            color={'$commentSectionTextColor'}
            marginLeft={5}
          >
            {content?.comments_count ?? 0}
          </Paragraph>
        </XStack>
      </Pressable>

      {/* The third XStack for handling shares */}
      <XStack
        testID="kisan_tId_HandleShare"
        onPress={() => handleShare(content)}
        flex={1}
        flexBasis={'0%'}
        justifyContent="flex-end"
      >
        <ShareRegular color={'$commentSectionTextColor'} size={22} />
        <Paragraph
          fontSize={16}
          fontWeight={'500'}
          color={'$commentSectionTextColor'}
          marginLeft={5}
        >
          {t('page.share')}
        </Paragraph>
      </XStack>
    </View>
  );
};
